// pages/AuthPage.jsx — styled to match home page

import React, { useState, useRef } from "react";
import LogoWordmark       from "../components/atoms/LogoWordmark";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { useTheme }       from "../context/ThemeContext";
import { useAuth  }       from "../context/AuthContext";

export default function AuthPage({ nav }) {
  const { t }                            = useTheme();
  const { login, register, googleLogin, updateProfile } = useAuth();

  const [mode,     setMode]     = useState("login");
  const [name,     setName]     = useState("");
  const [instituteName, setInstituteName] = useState("");
  const [educationLevel, setEducationLevel] = useState("");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [err,      setErr]      = useState("");
  const [busy,     setBusy]     = useState(false);
  const [profilePrompt, setProfilePrompt] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState("");

  // Ref prevents double-submit; does NOT go in useCallback deps
  const submitting = useRef(false);

  const switchMode = (m) => {
    setMode(m); setName(""); setInstituteName(""); setEducationLevel(""); setEmail(""); setPassword(""); setErr("");
    submitting.current = false;
  };

  const continueAfterAuth = (authenticatedUser) => {
    if (!authenticatedUser.instituteName || !authenticatedUser.educationLevel) {
      setProfilePrompt({
        instituteName: authenticatedUser.instituteName || "",
        educationLevel: authenticatedUser.educationLevel || "",
      });
      return;
    }
    nav("home");
  };

  const savePromptedProfile = async () => {
    if (!profilePrompt.instituteName.trim() || !profilePrompt.educationLevel) {
      setProfileError("Please add your institute and education level to continue.");
      return;
    }
    setProfileSaving(true);
    setProfileError("");
    try {
      await updateProfile(profilePrompt.instituteName.trim(), profilePrompt.educationLevel);
      setProfilePrompt(null);
      nav("home");
    } catch (e) {
      setProfileError(e.message || "Unable to update your profile.");
    } finally {
      setProfileSaving(false);
    }
  };

  // ── Email/password submit ─────────────────────────────────────────
  const handleSubmit = async () => {
    if (submitting.current) return;
    setErr("");
    if (!email.trim())    { setErr("Email is required."); return; }
    if (!password.trim()) { setErr("Password is required."); return; }
    if (mode === "signup") {
      if (!name.trim())        { setErr("Name is required."); return; }
      if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    }
    submitting.current = true;
    setBusy(true);
    try {
      const authenticatedUser = mode === "signup"
        ? await register(name.trim(), email.trim(), password, instituteName.trim(), educationLevel)
        : await login(email.trim(), password);
      continueAfterAuth(authenticatedUser);
    } catch (e) {
      setErr(e.message || "Something went wrong. Please try again.");
      submitting.current = false;
    } finally {
      setBusy(false);
    }
  };

  // ── Google sign-in ────────────────────────────────────────────────
  // NOT wrapped in useCallback — GoogleSignInButton uses a ref internally
  // so it doesn't matter if this function identity changes between renders.
  const handleGoogle = async (credential) => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setErr("");
    try {
      const authenticatedUser = await googleLogin(credential);
      continueAfterAuth(authenticatedUser);
    } catch (e) {
      setErr(e.message || "Google sign-in failed. Please try again.");
      submitting.current = false;
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box", height: 42,
    padding: "0 14px",
    border: `0.5px solid ${t.border2}`,
    borderRadius: 10, fontSize: 14, color: t.text,
    fontFamily: "inherit", outline: "none",
    background: t.inputBg,
    transition: "border-color 0.15s",
  };

  return (
    <div style={{ minHeight: "100vh", background: t.bg, paddingTop: 56 }}>
      <div style={{
        minHeight: "calc(100vh - 56px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "40px 24px",
      }}>
        <div style={{ width: "100%", maxWidth: 420 }}>

          {/* Logo */}
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
              <LogoWordmark onClick={() => nav("home")} />
            </div>
            <p style={{ fontSize: 13, color: t.text3, margin: 0 }}>Quantitative problem archive</p>
          </div>

          {/* Card */}
          <div style={{
            background: t.cardBg,
            border: `0.5px solid ${t.border}`,
            borderRadius: 18,
            boxShadow: "0 2px 20px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}>

            {/* Tabs */}
            <div style={{ display: "flex", borderBottom: `0.5px solid ${t.border}` }}>
              {[["login","Sign In"],["signup","Create Account"]].map(([k,l]) => (
                <button key={k} onClick={() => switchMode(k)} style={{
                  flex: 1, padding: "16px 0", border: "none", cursor: "pointer",
                  background: "transparent",
                  color: mode === k ? t.text : t.text3,
                  fontSize: 14, fontWeight: mode === k ? 600 : 400,
                  borderBottom: `2px solid ${mode === k ? t.text : "transparent"}`,
                  transition: "all 0.2s",
                }}>
                  {l}
                </button>
              ))}
            </div>

            <div style={{ padding: "28px 28px 24px" }}>

              {/* Google button — top, most prominent */}
              <div style={{ marginBottom: 22 }}>
                <GoogleSignInButton onSuccess={handleGoogle} onError={setErr} />
              </div>

              {/* Divider */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
                <div style={{ flex: 1, height: "0.5px", background: t.border }} />
                <span style={{ fontSize: 12, color: t.text3, letterSpacing: "0.05em" }}>OR</span>
                <div style={{ flex: 1, height: "0.5px", background: t.border }} />
              </div>

              {/* Fields */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                {mode === "signup" && (
                  <>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: t.text2, letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>FULL NAME</label>
                      <input
                        type="text" placeholder="Your full name"
                        value={name} onChange={(e) => setName(e.target.value)}
                        autoComplete="name" style={inputStyle}
                        onFocus={(e) => (e.target.style.borderColor = t.text)}
                        onBlur={(e)  => (e.target.style.borderColor = t.border2)}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: t.text2, letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>INSTITUTE NAME</label>
                      <input
                        type="text" placeholder="Your institute or university"
                        value={instituteName} onChange={(e) => setInstituteName(e.target.value)}
                        autoComplete="organization" style={inputStyle}
                        onFocus={(e) => (e.target.style.borderColor = t.text)}
                        onBlur={(e)  => (e.target.style.borderColor = t.border2)}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: t.text2, letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>EDUCATION LEVEL</label>
                      <select value={educationLevel} onChange={(e) => setEducationLevel(e.target.value)} style={inputStyle}>
                        <option value="">Select your level</option>
                        {['Undergraduate', '12th', '10th', 'Postgraduate', 'Schooling', 'PhD'].map((level) => <option key={level} value={level}>{level}</option>)}
                      </select>
                    </div>
                  </>
                )}

                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: t.text2, letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>EMAIL</label>
                  <input
                    type="email" placeholder="you@example.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email" style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = t.text)}
                    onBlur={(e)  => (e.target.style.borderColor = t.border2)}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: t.text2, letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>PASSWORD</label>
                  <input
                    type="password"
                    placeholder={mode === "signup" ? "Minimum 6 characters" : "Your password"}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    autoComplete={mode === "signup" ? "new-password" : "current-password"}
                    style={inputStyle}
                    onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                    onFocus={(e) => (e.target.style.borderColor = t.text)}
                    onBlur={(e)  => (e.target.style.borderColor = t.border2)}
                  />
                </div>

                {/* Error */}
                {err && (
                  <div style={{ background: "#fdf0f0", border: "0.5px solid #f5c6c6", borderRadius: 8, padding: "10px 12px" }}>
                    <p style={{ fontSize: 13, color: "#c0392b", margin: 0, lineHeight: 1.4 }}>{err}</p>
                  </div>
                )}

                {/* Submit */}
                <button onClick={handleSubmit} disabled={busy} style={{
                  background: busy ? t.bg2 : t.primary,
                  color: busy ? t.text3 : t.primaryFg,
                  border: "none", borderRadius: 10,
                  padding: "13px 0", fontSize: 14, fontWeight: 600,
                  cursor: busy ? "not-allowed" : "pointer",
                  transition: "all 0.2s", marginTop: 2,
                }}>
                  {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
                </button>
              </div>

              {/* Switch */}
              <p style={{ fontSize: 12, color: t.text3, textAlign: "center", marginTop: 20, marginBottom: 0 }}>
                {mode === "login"
                  ? <>Don't have an account?{" "}<button onClick={() => switchMode("signup")} style={{ background: "none", border: "none", color: t.text, fontWeight: 600, fontSize: 12, cursor: "pointer", padding: 0, textDecoration: "underline" }}>Create one</button></>
                  : <>Already have an account?{" "}<button onClick={() => switchMode("login")} style={{ background: "none", border: "none", color: t.text, fontWeight: 600, fontSize: 12, cursor: "pointer", padding: 0, textDecoration: "underline" }}>Sign in</button></>
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {profilePrompt && (
        <div role="dialog" aria-modal="true" aria-labelledby="profile-prompt-title" style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "rgba(15, 17, 23, 0.45)" }}>
          <div style={{ width: "100%", maxWidth: 430, padding: 28, borderRadius: 16, background: t.cardBg, border: `1px solid ${t.border}`, boxShadow: "0 18px 60px rgba(0,0,0,0.2)" }}>
            <p style={{ margin: "0 0 8px", color: t.text3, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em" }}>COMPLETE YOUR PROFILE</p>
            <h2 id="profile-prompt-title" style={{ margin: "0 0 8px", color: t.text, fontSize: 22 }}>Tell us about your education</h2>
            <p style={{ margin: "0 0 22px", color: t.text2, fontSize: 13, lineHeight: 1.5 }}>Add your college and education level so your QuantSoc profile is complete.</p>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <label style={{ color: t.text2, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em" }}>
                INSTITUTE NAME
                <input
                  value={profilePrompt.instituteName}
                  onChange={(e) => setProfilePrompt({ ...profilePrompt, instituteName: e.target.value })}
                  placeholder="Your institute or university"
                  maxLength={120}
                  autoFocus
                  style={{ ...inputStyle, marginTop: 7 }}
                />
              </label>
              <label style={{ color: t.text2, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em" }}>
                EDUCATION LEVEL
                <select
                  value={profilePrompt.educationLevel}
                  onChange={(e) => setProfilePrompt({ ...profilePrompt, educationLevel: e.target.value })}
                  style={{ ...inputStyle, marginTop: 7 }}
                >
                  <option value="">Select your level</option>
                  {['Undergraduate', '12th', '10th', 'Postgraduate', 'Schooling', 'PhD'].map((level) => <option key={level} value={level}>{level}</option>)}
                </select>
              </label>
            </div>
            {profileError && <p style={{ margin: "14px 0 0", color: "#c0392b", fontSize: 13 }}>{profileError}</p>}
            <button onClick={savePromptedProfile} disabled={profileSaving} style={{ width: "100%", marginTop: 22, padding: "12px 0", border: "none", borderRadius: 10, background: profileSaving ? t.bg2 : t.primary, color: profileSaving ? t.text3 : t.primaryFg, fontSize: 14, fontWeight: 600, cursor: profileSaving ? "wait" : "pointer" }}>
              {profileSaving ? "Saving..." : "Save and continue"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
